import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dropdown, Space, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import './ActionStack.scss';
export const ActionStack = ({ actions, maxVisible = 3, size = 'middle', direction = 'horizontal', variant = 'compact', gap = 'small', showTooltips = false, iconOnly = false, className = '', style = {}, loading = false, disabled = false, dropdownPlacement = 'bottomRight' }) => {
    // Filter visible actions
    const visibleActions = actions.filter((action) => !action.hidden);
    // Split actions into visible and dropdown
    const primaryActions = visibleActions.slice(0, maxVisible);
    const dropdownActions = visibleActions.slice(maxVisible);
    // Handle action click
    const handleActionClick = async (action) => {
        if (action.disabled || disabled || loading)
            return;
        try {
            await action.onClick?.();
        }
        catch (error) {
            console.error(`Error executing action ${action.key}:`, error);
        }
    };
    // Generate button props based on variant and action
    const getButtonProps = (action) => {
        const baseProps = {
            key: action.key,
            size,
            danger: action.danger,
            disabled: action.disabled || disabled || loading,
            loading: action.loading || loading,
            icon: action.icon,
            onClick: () => handleActionClick(action)
        };
        // Apply variant styling
        if (action.variant === 'filled' && action.color) {
            return {
                ...baseProps,
                type: 'primary',
                style: {
                    backgroundColor: action.color,
                    borderColor: action.color,
                    color: '#ffffff'
                }
            };
        }
        if (action.variant === 'outlined' && action.color) {
            return {
                ...baseProps,
                type: 'default',
                style: {
                    borderColor: action.color,
                    color: action.color,
                    backgroundColor: 'transparent'
                }
            };
        }
        if (action.variant === 'text' && action.color) {
            return {
                ...baseProps,
                type: 'text',
                style: {
                    color: action.color
                }
            };
        }
        if (action.variant === 'link' && action.color) {
            return {
                ...baseProps,
                type: 'link',
                style: {
                    color: action.color
                }
            };
        }
        return {
            ...baseProps,
            type: action.type || 'default'
        };
    };
    // Render individual action button
    const renderActionButton = (action) => {
        const buttonProps = getButtonProps(action);
        const button = _jsx(Button, { ...buttonProps, children: !iconOnly && action.label });
        // Always show tooltip when iconOnly is true, or when explicitly requested
        if (iconOnly || showTooltips || action.tooltip) {
            const tooltipTitle = action.tooltip || action.label;
            return (_jsx(Tooltip, { title: tooltipTitle, placement: 'top', children: button }, action.key));
        }
        return button;
    };
    // Create dropdown menu items
    const dropdownMenuItems = dropdownActions.map((action) => ({
        key: action.key,
        label: action.label,
        icon: action.icon,
        disabled: action.disabled || disabled || loading,
        danger: action.danger,
        onClick: () => handleActionClick(action)
    }));
    // Handle dropdown menu click
    const handleDropdownClick = ({ key }) => {
        const action = dropdownActions.find((a) => a.key === key);
        if (action) {
            handleActionClick(action);
        }
    };
    // Get container classes
    const containerClasses = [
        'action-stack',
        `action-stack-${variant}`,
        `action-stack-${direction}`,
        `action-stack-${size}`,
        iconOnly && 'action-stack-icon-only',
        loading && 'action-stack-loading',
        disabled && 'action-stack-disabled',
        className
    ]
        .filter(Boolean)
        .join(' ');
    // No actions to render
    if (visibleActions.length === 0) {
        return null;
    }
    return (_jsx("div", { className: containerClasses, style: style, children: _jsxs(Space, { direction: direction, size: gap, className: 'action-stack-content', children: [primaryActions.map(renderActionButton), dropdownActions.length > 0 && (_jsx(Dropdown, { menu: {
                        items: dropdownMenuItems,
                        onClick: handleDropdownClick
                    }, placement: dropdownPlacement, trigger: ['click'], disabled: disabled || loading, children: _jsx(Button, { size: size, icon: _jsx(MoreOutlined, {}), disabled: disabled || loading, className: 'action-stack-more' }) }))] }) }));
};
